<template>
  <section class="h-full mx-4">
    <BaseTable
      v-if="vatLevels"
      :headers="vatLevelsHeaders"
      :list="vatLevels"
      clickable
      @add-new="onAddNewVatLevel"
      @table-item-clicked="onEditVatLevel"
      @deleteMultiselect="onDeleteMultiselect"
      @refresh-data="loadData"
      table-id="vat-levels"
      @apply-filters="onApplyFilters"
      @reset-filters="onResetFilters">
      <template #actions="{ item }: any">
        <div class="flex items-center justify-center gap-1">
          <ActionButton
            :isEnabled="item.isFromManager"
            color="red"
            :icon="IconTrashOutline"
            @action="onDeleteRow(item as VatLevel)" />
        </div>
      </template>
      <template #filters>
        <section class="flex flex-col items-start justify-start w-full max-w-xl py-4 mx-auto">
          <div>
            <label class="space-y-2 text-sm tracking-wide text-gray-400 uppercase">{{
              $t('baseObject.isActive')
            }}</label>
            <BaseTriStateToggle
              :label="$t('baseObject.isActive')"
              name="isActive"
              v-model="filterIsActive" />
          </div>
        </section>
      </template>
    </BaseTable>

    <VatLevelsModalCreateEdit
      v-if="createEditModalActive"
      :mode="createEditModalMode"
      :vat-level="vatLevel"
      @close-modal="createEditModalActive = false"
      @save="onSave">
    </VatLevelsModalCreateEdit>
    <BaseModalConfirm
      v-if="modalDeleteRow"
      @cancel="modalDeleteRow = false"
      @confirm="onDeleteSingle()">
      <template #title>{{ $t('common.actions.delete') }}</template>
      <template #text>
        {{ $t('messages.info.deleteSingle', { 'item': deleteRowItem?.name }) }}
      </template>
      <template #buttonText>{{ $t('common.actions.delete') }}</template>
    </BaseModalConfirm>
  </section>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { v4 as uuidv4 } from 'uuid';
import { useToastStore } from '@/stores/toast';
import { useI18n } from 'vue-i18n';

import { getVatLevels, removeVatLevelsByIds } from '@/services/api/vat';
import { filterActive, filterData, sortData } from '@/utils/table';
import VatLevelsModalCreateEdit from '@/components/prices/VatLevelsModalCreateEdit.vue';
import BaseTable from '@/components/common/BaseTable.vue';
import BaseModalConfirm from '@/components/common/BaseModalConfirm.vue';
import ActionButton from '@/components/common/ActionButton.vue';
import IconTrashOutline from '@/assets/icons/IconTrashOutline.vue';
import BaseTriStateToggle from '@/components/common/BaseTriStateToggle.vue';

import type { TableFilterData } from '@/types/Base';
import type { VatLevel } from '@/types/Vat';
import { useLookupsStore } from '@/stores/lookups';

const { t: $t } = useI18n({ useScope: 'global' });

const toastStore = useToastStore();
const { reloadLookups } = useLookupsStore();

const vatLevels = ref<VatLevel[]>([]);
const filterIsActive = ref<boolean | undefined>();

const vatLevelsHeaders = [
  { value: 'code', text: $t('baseObject.code'), type: 'text', active: true },
  { value: 'name', text: $t('baseObject.name'), type: 'text', active: true },
  { value: 'boid', text: $t('baseObject.boid'), type: 'text', active: true },
  { value: 'isActive', text: $t('baseObject.isActive'), type: 'check', active: true },
  { value: 'actions', text: $t('baseObject.actions'), active: true },
];

onMounted(async () => {
  await loadData();
});

const vatLevel = ref<VatLevel>(initializeVatLevel());

const createEditModalActive = ref(false);

const createEditModalMode = ref<'add' | 'edit'>('add');

function initializeVatLevel(): VatLevel {
  return {
    id: uuidv4(),
    name: '',
    code: '',
    isActive: true,
    isFromManager: true,
  };
}

function onAddNewVatLevel() {
  vatLevel.value = initializeVatLevel();
  createEditModalActive.value = true;
  createEditModalMode.value = 'add';
}

function onEditVatLevel(item: VatLevel) {
  vatLevel.value = item;
  createEditModalActive.value = true;
  createEditModalMode.value = 'edit';
}

async function onSave(modifiedData: VatLevel) {
  if (!vatLevels.value) return;

  const index = vatLevels.value.findIndex((item) => item.id === modifiedData.id);
  if (index !== -1) vatLevels.value[index] = modifiedData;
  else vatLevels.value.push(modifiedData);
  createEditModalActive.value = false;

  await reloadLookups(['vatLevels']);
}

async function loadData(data?: TableFilterData) {
  let response = await getVatLevels();
  if (!response) return;

  if (data && data.query) {
    response = filterData(response, data.query, ['name', 'code']);
  }

  if (data && data.column !== undefined && data.order) {
    response = sortData(response, data.column, data.order, vatLevelsHeaders);
  }

  if (data && data.isActive !== undefined) response = filterActive(response, data.isActive);

  vatLevels.value = response;
}

async function onDeleteMultiselect(items: VatLevel[]) {
  const idsToDelete = items.filter((item) => item.isFromManager === true).map((item) => item.id);
  const res = await removeVatLevelsByIds(idsToDelete);
  if (!res && vatLevels.value)
    vatLevels.value = vatLevels.value.filter((type) => !idsToDelete.includes(type.id));
  res
    ? toastStore.error({ text: res })
    : toastStore.success({ text: $t('messages.success.successfullyDeleted') });

  await reloadLookups(['vatLevels']);
}

async function onDeleteSingle() {
  modalDeleteRow.value = false;
  if (!deleteRowItem.value) return;
  const idToDelete = deleteRowItem.value.id;
  const res = await removeVatLevelsByIds([idToDelete]);
  if (!res && vatLevels.value)
    vatLevels.value = vatLevels.value.filter((type) => type.id !== idToDelete);
  res
    ? toastStore.error({ text: res })
    : toastStore.success({ text: $t('messages.success.successfullyDeleted') });

  await reloadLookups(['vatLevels']);
}

const modalDeleteRow = ref(false);

const deleteRowItem = ref<VatLevel>();
function onDeleteRow(item: VatLevel) {
  deleteRowItem.value = item;
  modalDeleteRow.value = true;
}

function onApplyFilters(data: TableFilterData) {
  data.isActive = filterIsActive.value ?? undefined;
  loadData(data);
}

function onResetFilters() {
  filterIsActive.value = undefined;
  loadData({});
}
</script>
